/**
 * OffersListPage - Unified offers list page with role-aware views
 *
 * Route: /offers
 *
 * URL State:
 * - statuses: comma-separated (default: "pending,engaged,accepted", not in URL)
 * - sort: field:direction (default: "created_at:desc", not in URL)
 * - search: string (default: empty, not in URL)
 *
 * Role-based behavior:
 * - Investor: View their offers across all properties
 * - Agent: View offers on their properties
 * - Admin: View all offers
 */

import { useState, useCallback, useMemo } from 'react'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { useToast } from '@/common/hooks/useToast'
import { useMediaQueryToken } from '@/common/hooks/useMediaQuery'
import { DataGrid } from '@/common/components/data-grid'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { sprinkles } from '@/core/styles/sprinkles.css'
import { getErrorMessage } from '@/common/utils/errors'
import { unorderedArraysEqual } from '@/common/utils/arrays'
import { useOffersSource } from '@/features/offers/data/offersSource'
import { OffersToolbar } from '@/features/offers/components/OffersToolbar'
import { OfferCard } from '@/features/offers/components/OfferCard'
import { OfferDetailDialog } from '@/features/offers/components/OfferDetailDialog'
import {
  useAgentTrainingMode,
  useAgentTrainingTourContext,
} from '@/features/agent-training-tour'
import { resolveContractVariant } from '@/features/offers/utils/contractVariant'
import {
  acceptOffer,
  rejectOffer,
  engageOffer,
  submitCounterOffer,
  type OfferWithPropertyResponse,
  type CounterOfferCreate,
} from '@/features/offers/api/offers'
import { CounterOfferDialog } from '@/features/offers/components/CounterOfferDialog'
import type { OfferFilters, OfferStatus } from '@/features/offers/data/offersSource'

export const Route = createFileRoute({
  component: OffersListPage,
})

// =============================================================================
// Defaults
// =============================================================================

const DEFAULT_STATUSES: OfferStatus[] = ['pending', 'engaged', 'accepted']
const DEFAULT_SORT = 'created_at:desc'

// =============================================================================
// URL State Helpers
// =============================================================================

function parseUrlStatuses(value: string | null): OfferStatus[] {
  if (!value) return DEFAULT_STATUSES
  const statuses = value.split(',').filter(Boolean) as OfferStatus[]
  return statuses.length > 0 ? statuses : DEFAULT_STATUSES
}

function parseUrlSort(value: string | null): string {
  return value ?? DEFAULT_SORT
}

// =============================================================================
// Main Component
// =============================================================================

function OffersListPage() {
  const { roles, token } = useAuth()
  const { data: userProfile } = useUserProfile()
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isTabletDown = useMediaQueryToken('tabletDown')
  const search = useSearch({ strict: false }) as {
    statuses?: string
    sort?: string
    search?: string
  }
  const [processingOfferId, setProcessingOfferId] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [detailOffer, setDetailOffer] = useState<OfferWithPropertyResponse | null>(null)
  const [counterOfferDialogOpen, setCounterOfferDialogOpen] = useState(false)
  const [counterOfferTarget, setCounterOfferTarget] = useState<OfferWithPropertyResponse | null>(null)

  // Parse URL state
  const urlStatuses = parseUrlStatuses(search.statuses ?? null)
  const urlSort = parseUrlSort(search.sort ?? null)
  const urlSearch = search.search ?? ''

  // Convert URL state to filters
  const filters = useMemo<OfferFilters>(
    () => ({ statuses: urlStatuses }),
    [urlStatuses]
  )

  // Parse sort into field/direction
  const initialSort = useMemo(() => {
    const [field, direction] = urlSort.split(':')
    return { field, direction: direction as 'asc' | 'desc' }
  }, [urlSort])

  // MRG-281: in-training external agents see hardcoded fixture offers
  // (the demo investor offers shipped with the guided tour) instead of
  // real DB rows.
  const trainingMode = useAgentTrainingMode()
  // Step 4 of the guided tour spotlights the action menu on the first
  // offer card with all options (Engage / Accept / Reject) visible —
  // see Figma frame 54:383. We force-open the dropdown for the first
  // card while that phase is active.
  const { currentPhase } = useAgentTrainingTourContext()
  const shouldForceFirstCardMenuOpen =
    trainingMode && currentPhase === 'step-4-offers-actions'

  // Create source with URL-derived initial state
  const source = useOffersSource({ filters, trainingMode })

  // Update URL params helper
  const updateUrl = useCallback(
    (updates: { statuses?: OfferStatus[]; sort?: string; search?: string }) => {
      navigate({
        search: ((prev: Record<string, string | undefined>) => {
          const next = { ...prev }
          // Statuses: only set if different from default
          if (updates.statuses !== undefined) {
            if (unorderedArraysEqual(updates.statuses, DEFAULT_STATUSES)) {
              delete next.statuses
            } else if (updates.statuses.length > 0) {
              next.statuses = updates.statuses.join(',')
            } else {
              delete next.statuses
            }
          }
          // Sort: only set if different from default
          if (updates.sort !== undefined) {
            if (updates.sort === DEFAULT_SORT) {
              delete next.sort
            } else {
              next.sort = updates.sort
            }
          }
          // Search: only set if not empty
          if (updates.search !== undefined) {
            if (updates.search === '') {
              delete next.search
            } else {
              next.search = updates.search
            }
          }
          return next
        }) as never,
        replace: true,
      })
    },
    [navigate]
  )

  // Handle filter change from toolbar
  const handleFiltersChange = useCallback(
    (newFilters: OfferFilters) => {
      updateUrl({ statuses: newFilters.statuses ?? [] })
    },
    [updateUrl]
  )

  // Handle sort change from toolbar
  const handleSortChange = useCallback(
    (value: string) => {
      updateUrl({ sort: value })
    },
    [updateUrl]
  )

  // Handle search change from toolbar
  const handleSearchChange = useCallback(
    (value: string) => {
      updateUrl({ search: value })
    },
    [updateUrl]
  )

  // Determine user role for view customization
  const isAgent = roles.includes('agent')
  const isInvestor = roles.includes('investor')
  const isAdmin = roles.includes('admin')
  const contractVariant = resolveContractVariant({
    roles,
    agentType: userProfile?.profile?.agent_type,
  })

  // Page title and description based on role
  const pageTitle = isAgent || isAdmin ? 'Offers on my properties' : 'My offers'
  const pageDescription =
    isAgent || isAdmin
      ? 'Review and respond to offers from investors on your properties.'
      : 'Track your offers across all properties.'

  // Refresh data after action
  const refreshData = useCallback(() => {
    setRefreshKey((k) => k + 1)
  }, [])

  // Handle accept offer (Agent)
  const handleAccept = useCallback(
    async (offer: OfferWithPropertyResponse) => {
      if (!token || processingOfferId) return

      setProcessingOfferId(offer.id)
      try {
        await acceptOffer(token, offer.id)
        showSuccessToast('Offer accepted!')
        // Invalidate caches BEFORE refreshing so we get fresh data
        await queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
        queryClient.invalidateQueries({ queryKey: ['property', offer.property_id] })
        refreshData()
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to accept offer')
      } finally {
        setProcessingOfferId(null)
      }
    },
    [token, processingOfferId, showSuccessToast, showErrorToast, refreshData, queryClient]
  )

  // Handle reject offer (Agent)
  const handleReject = useCallback(
    async (offer: OfferWithPropertyResponse) => {
      if (!token || processingOfferId) return

      setProcessingOfferId(offer.id)
      try {
        await rejectOffer(token, offer.id)
        showSuccessToast('Offer rejected')
        // Invalidate cache BEFORE refreshing so we get fresh data
        await queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
        refreshData()
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to reject offer')
      } finally {
        setProcessingOfferId(null)
      }
    },
    [token, processingOfferId, showSuccessToast, showErrorToast, refreshData, queryClient]
  )

  // Handle engage offer (Agent)
  const handleEngage = useCallback(
    async (offer: OfferWithPropertyResponse) => {
      if (!token || processingOfferId) return

      setProcessingOfferId(offer.id)
      try {
        await engageOffer(token, offer.id)
        showSuccessToast('Offer engaged!')
        await queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
        refreshData()
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to engage offer')
      } finally {
        setProcessingOfferId(null)
      }
    },
    [token, processingOfferId, showSuccessToast, showErrorToast, refreshData, queryClient]
  )

  const openCounterOffer = useCallback(
    (offer: OfferWithPropertyResponse) => {
      setCounterOfferTarget(offer)
      setCounterOfferDialogOpen(true)
    },
    []
  )

  const handleCounterOffer = useCallback(
    async (offerId: string, data: CounterOfferCreate) => {
      if (!token) return
      await submitCounterOffer(token, offerId, data)
      showSuccessToast('Counter-offer submitted!')
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
      queryClient.invalidateQueries({ queryKey: ['negotiations', offerId] })
      refreshData()
    },
    [token, showSuccessToast, queryClient, refreshData]
  )

  return (
    <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
      {/* Page Header */}
      <Stack gap="4">
        <Text as="h1" variant="h4" weight="bold">
          {pageTitle}
        </Text>
        <Text
          variant="bodySm"
          color="muted"
          className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
        >
          {pageDescription}
        </Text>
      </Stack>

      {/* DataGrid */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          key={refreshKey}
          source={source}
          getItemKey={(o) => o.id}
          initialSort={initialSort}
          initialFilters={filters as Record<string, unknown>}
          initialSearch={urlSearch}
          columns={isTabletDown ? 1 : 'auto-fill'}
          minItemWidth={isTabletDown ? undefined : '400px'}
          gap={isTabletDown ? '12' : '16'}
          emptyMessage="No offers found"
          renderItem={(offer, index) => (
            <OfferCard
              offer={offer}
              isInvestor={isInvestor}
              isAgent={isAgent || isAdmin}
              isProcessing={processingOfferId === offer.id}
              onAccept={handleAccept}
              onReject={handleReject}
              onEngage={handleEngage}
              onCounterOffer={openCounterOffer}
              onClick={setDetailOffer}
              forceMenuOpen={shouldForceFirstCardMenuOpen && index === 0}
            />
          )}
        >
          {/* Toolbar with filters and search */}
          <DataGrid.Toolbar>
            <OffersToolbar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              sort={urlSort}
              onSortChange={handleSortChange}
              search={urlSearch}
              onSearchChange={handleSearchChange}
            />
          </DataGrid.Toolbar>
        </DataGrid>
      </div>

      <OfferDetailDialog
        open={!!detailOffer}
        onOpenChange={(open) => { if (!open) setDetailOffer(null) }}
        offer={detailOffer}
        token={token}
        contractVariant={contractVariant}
        actions={(isAgent || isAdmin) ? {
          onEngage: handleEngage,
          onAccept: handleAccept,
          onReject: handleReject,
          onCounterOffer: openCounterOffer,
        } : undefined}
      />

      {counterOfferTarget && (
        <CounterOfferDialog
          open={counterOfferDialogOpen}
          onOpenChange={setCounterOfferDialogOpen}
          offer={counterOfferTarget}
          onSubmit={handleCounterOffer}
        />
      )}
    </Stack>
  )
}
